import { FloorRule } from '@pubstack/common/src/adm/rules'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import Chip from '~/components/Chip'
import { RuleDetail, RuleDetailsFlyout } from '~/modules/adm/rules/RuleDetailsFlyout'
import { useProduct } from '~/hooks/useProduct'

type FloorRuleFlyoutPage = {
  floorRule: Omit<FloorRule, 'creationTime' | 'technicalId'>
  currencySymbol?: string
  onEditFloorRule: (floorRuleId: string) => void
  canEdit: boolean
}

/**
 * A floor rule, read the way every other rule is read.
 *
 * This used to be its own flyout — its own headings, its own spacing, its own section borders, and no
 * mention of what the rule is used by — while lazy loading, refresh and bidder rules had converged on one
 * layout. A rule is a rule: a publisher who has read one of these should not have to learn a second
 * shape for the third one. So the settings become `details` rows and the shared flyout draws them, which
 * also gives floor rules the "Used by" count the others carry.
 *
 * What is floor-specific stays here: the currency, the per-target chips, and the two fields Node has no
 * concept of — inventory sizes and the dynamic floor are One's alone.
 */
export const FloorRuleFlyoutPage: FunctionComponent<FloorRuleFlyoutPage> = ({ floorRule, currencySymbol, onEditFloorRule, canEdit }) => {
  const { product } = useProduct()

  // Stored in cents, read in the scope's own currency.
  const price = (cents: number) => `${cents / 100} ${currencySymbol ?? ''}`.trim()

  const details: RuleDetail[] = [
    { label: 'Default', value: price(floorRule.floor) },
    /* One row per target, numbered: the shared flyout keys its rows by label, so repeating "Target" would
       collide, and a reader referring to "the second target" has something to point at. */
    ...(floorRule.settings ?? []).map((setting, index) => ({
      label: `Target ${index + 1}`,
      value: (
        <div className={'flex flex-col gap-2'}>
          <div className={'flex flex-row flex-wrap gap-1'}>
            {setting.mediaTypes.map((mediaType) => (
              <Chip key={mediaType} className={'w-fit flex-row'} text={mediaType} color={Colors.Ghost} />
            ))}
            {/* Sizes are One's: a Node floor rule targets media types only, and printing an empty size list
                there would invent a distinction the product does not have. */}
            {product !== 'pbs' &&
              (setting.sizes.length === 0 ? (
                <Chip className={'w-fit flex-row text-slate-grey italic'} text={'All inventory sizes selected'} color={Colors.Ghost} />
              ) : (
                setting.sizes.map((size) => <Chip key={size} className={'w-fit flex-row'} text={size} color={Colors.Ghost} />)
              ))}
          </div>
          <span>{price(setting.floor)}</span>
        </div>
      ),
    })),
    ...(product !== 'pbs' ? [{ label: 'Dynamic floor', value: floorRule.dynamic ? 'On' : 'Off' }] : []),
  ]

  return (
    <RuleDetailsFlyout
      name={floorRule.name}
      details={details}
      stacksUse={floorRule.stacksUse}
      onEdit={canEdit ? () => onEditFloorRule(floorRule.id) : undefined}
      editDisabledReason={'This is a Pubstack rule, so it cannot be edited.'}
      editTrackingLabel={'adm-floor-price-rules-edit'}
    />
  )
}
